import { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { pget, ppost, communityLogoUrl, rememberPortalBrand } from "./portalApi";
import { homeLabel } from "@/lib/home";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { BrandFont } from "@/components/BrandFont";
import { useSidebarCollapse } from "@/hooks/useSidebarCollapse";
import { SidebarCollapseToggle, SidebarHint } from "@/components/SidebarCollapse";
import { navBrand, useBrandTheme } from "@/lib/brand";
import { makeVocab, setActiveVocab } from "@/lib/vocab";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Home, Wrench, CalendarCheck, CalendarDays, FileText, LogOut, Menu, X,
  MessageSquareText, PhoneCall, BarChart3, ChevronDown, Check, Contact, Bell, ShieldAlert, Megaphone, UserCog,
} from "lucide-react";

// NAV paths are relative to the current portal base ("/portal" or "/c/:slug/portal").
// Each item can gate itself on a community-level feature flag so the sidebar reflects
// what the community has actually enabled — iter198m fix for the answers_only + modules
// hierarchy being ignored in the portal.
//   • hideIfAnswersOnly: hide when community.mode === "answers_only" (no ticket creation)
//   • requiresModule: hide when community.modules.<key> is falsy
//   • requiresContactsShared: hide when community.contacts_shared !== true
//   • iter396: community.pages[<to>] === false hides a page limited to audience chips this
//     resident doesn't carry (Settings → Concierge → Resident portal pages).
const NAV = [
  { to: "", end: true, icon: Home, label: "Home" },
  { to: "requests", icon: Wrench, label: "My Requests", hideIfAnswersOnly: true },
  { to: "amenities", icon: CalendarCheck, label: "Amenities", requiresModule: "amenities" },
  { to: "events", icon: CalendarDays, label: "Events", requiresModule: "events" },
  { to: "surveys", icon: BarChart3, label: "Surveys", requiresModule: "surveys" },
  { to: "documents", icon: FileText, label: "Documents" },
  { to: "contacts", icon: Contact, label: "Trusted Pros", requiresContactsShared: true },
  { to: "notifications", icon: Bell, label: "Notifications" },
  { to: "account", icon: UserCog, label: "Account" },
];

/** Which NAV items are visible for this community's current config. Single source of
 *  truth so PortalLayout, PortalHome, and any future route guard all match. */
export function navFor(community) {
  const c = community || {};
  const modules = c.modules || {};
  const pages = c.pages || {};
  const answersOnly = c.mode === "answers_only";
  return NAV.filter((n) => {
    // Requests stays visible in answers_only when Field Services is on (tracking-only mode):
    // residents can watch a dispatched job progress, they just can't file a ticket in the portal.
    if (n.hideIfAnswersOnly && answersOnly && !c.field_services) return false;
    if (n.requiresModule && !modules[n.requiresModule]) return false;
    if (n.requiresContactsShared && c.contacts_shared !== true) return false;
    if (n.to && pages[n.to] === false) return false;
    return true;
  });
}

export default function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [sidebarCollapsed, toggleSidebarCollapsed] = useSidebarCollapse();

  // The resident sees the same colour the office picked — every redline-* accent in the portal
  // follows the community colour (same palette logic as the staff console).
  useBrandTheme(me?.community?.brand_color);
  const pv = useMemo(() => setActiveVocab(me?.community), [me?.community]);
  const navSel = me?.community?.brand_color ? navBrand(me.community.brand_color) : null;
  const [logoBroken, setLogoBroken] = useState(false);
  useEffect(() => { if (me?.community) rememberPortalBrand(me.community); }, [me?.community]);

  // Derive the current portal base so this layout renders correctly whether
  // mounted at /portal/* (root) or /c/:slug/portal/* (path-based white-label).
  const portalBase = useMemo(() => {
    const m = location.pathname.match(/^\/c\/([^/]+)\/portal/);
    return m ? `/c/${m[1]}/portal` : "/portal";
  }, [location.pathname]);
  const joinBase = (rel) => (rel ? `${portalBase}/${rel}` : portalBase);
  // Rewrite an absolute /portal/... path to the current base (so bell links
  // land back on /c/:slug/portal/... when we're in slug context).
  const rebase = (to) => {
    if (!to) return portalBase;
    if (to.startsWith("/portal")) return portalBase + to.slice("/portal".length);
    return to;
  };

  useEffect(() => {
    pget("/resident/me").then((r) => setMe(r.data)).catch(() => navigate("/portal/login")).finally(() => setLoading(false));
  }, [navigate]);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  // iter396 — a page limited to chips this resident doesn't carry isn't reachable by URL either.
  useEffect(() => {
    const pages = me?.community?.pages;
    if (!pages) return;
    const seg = location.pathname.replace(/\/+$/, "").split("/portal/")[1] || "";
    const key = seg.split("/")[0];
    if (key && pages[key] === false) navigate(rebase(""), { replace: true });
  }, [me, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => {
    try { await ppost("/resident/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("askredline.residentToken");
    navigate("/portal/login");
  };

  const exitImpersonation = async () => {
    try { await ppost("/resident/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("askredline.residentToken");
    window.location.href = "/dashboard";
  };

  // Multi-property owners: flip the active membership, then hard-reload so every page refetches.
  const [switching, setSwitching] = useState(false);

  // In-app bell: derived feed of announcements + this household's request updates.
  const [bell, setBell] = useState({ items: [], unread: 0 });
  useEffect(() => {
    if (!me) return;
    const loadBell = () => pget("/resident/notifications").then((r) => setBell(r.data)).catch(() => {});
    loadBell();
    const t = setInterval(loadBell, 60000);
    return () => clearInterval(t);
  }, [!!me]);
  const markBellRead = () => {
    ppost("/resident/notifications/read").catch(() => {});
    setBell((b) => ({ ...b, unread: 0 }));
  };
  const switchProperty = async (m) => {
    if (m.active || switching) return;
    setSwitching(true);
    try {
      await ppost("/resident/switch", { household_id: m.household_id });
      window.location.reload();
    } catch { setSwitching(false); }
  };

  if (loading || !me) {
    return <div className="min-h-screen bg-[#f7f4f0] flex items-center justify-center"><div className="h-7 w-7 rounded-full border-2 border-redline-500 border-t-transparent animate-spin" /></div>;
  }

  const first = (me.person?.name || "neighbor").split(" ")[0];
  const num = me.community?.phone_number || "";
  const logoUrl = communityLogoUrl(me.community);
  const multi = (me.memberships || []).length > 1;
  const navLabel = (n) => (n.to === "requests" && ((me.community?.requests_label || "").trim() || (!pv.isHoa && pv.mod("portal_requests")))) || (n.to === "contacts" && !pv.isHoa && pv.mod("contacts")) || n.label;

  return (
    <div className="min-h-screen flex bg-[#f7f4f0] font-body" data-brandfont>
      <BrandFont brand={me?.community} />
      {open && <div data-testid="portal-backdrop" onClick={() => setOpen(false)} className="fixed inset-0 bg-ink/50 z-40 lg:hidden" />}

      <aside id="resident-sidebar" data-collapsed={sidebarCollapsed} className={`app-sidebar w-64 shrink-0 bg-[#1a1614] text-sand-100 flex flex-col fixed h-screen z-50 transition-transform duration-300 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="portal-sidebar">
        {/* The resident's community owns the top of the sidebar — its logo (or initial in its colour),
            its name — with Ask Redline as the quiet "powered by" line, exactly like the staff console. */}
        <div className="sidebar-identity p-4 border-b border-white/10 flex items-center gap-3" data-testid="portal-community-identity" title={`${me.community?.name}${homeLabel(me.household) ? ` · ${homeLabel(me.household)}` : ''}`}>
          {logoUrl && !logoBroken ? (
            <img src={logoUrl} alt="" onError={() => setLogoBroken(true)} data-testid="portal-sidebar-logo"
              className="h-10 w-10 shrink-0 rounded-xl bg-white/95 object-contain p-0.5" />
          ) : (
            <div data-testid="portal-sidebar-mark"
              className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center font-heading font-extrabold text-base"
              style={{ backgroundColor: navSel?.bg || "#D32F2F", color: navSel?.fg || "#ffffff" }}>
              {((me.community?.name || "?").trim()[0] || "?").toUpperCase()}
            </div>
          )}
          <div className="sidebar-label min-w-0 flex-1 leading-tight">
            <div className="font-heading font-extrabold text-white text-sm line-clamp-2" title={me.community?.name} data-testid="portal-sidebar-community">{me.community?.name}</div>
            <div className="text-[9px] uppercase tracking-[0.12em] text-sand-400 mt-0.5 leading-snug">{pv.one("portal")} · Powered by Ask Redline</div>
          </div>
          <button data-testid="portal-sidebar-close" onClick={() => setOpen(false)} className="lg:hidden text-sand-300 hover:text-white shrink-0"><X className="h-5 w-5" /></button>
        </div>
        {(multi || me.household?.unit) && (
        <div className={`${multi ? 'sidebar-footer' : 'sidebar-label'} px-4 pt-4 pb-3 border-b border-white/10`}>
          {multi ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="portal-property-switcher" disabled={switching} aria-label={`Switch property: ${me.community?.name}`} title={`Switch property: ${me.community?.name}`}
                  className="sidebar-nav-item w-full flex items-center justify-between gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 hover:bg-white/10 transition-colors text-left disabled:opacity-60">
                  {sidebarCollapsed && <Home className="h-5 w-5" strokeWidth={1.5} />}
                  <span className="sidebar-label min-w-0">
                    <span className="block text-[9px] uppercase tracking-[0.15em] text-sand-400">Community</span>
                    <span className="block font-heading font-bold text-white text-sm truncate">{me.community?.name}</span>
                    {homeLabel(me.household) && <span className="block text-xs text-sand-400 mt-0.5">{homeLabel(me.household)}</span>}
                  </span>
                  <ChevronDown className="sidebar-label h-4 w-4 text-sand-300 shrink-0" strokeWidth={1.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                <div className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground">Your properties</div>
                {me.memberships.map((m) => (
                  <DropdownMenuItem key={m.household_id} data-testid={`portal-property-${m.household_id}`} onClick={() => switchProperty(m)}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{m.community}</div>
                      {homeLabel(m) && <div className="text-xs text-muted-foreground">{homeLabel(m)}</div>}
                    </div>
                    {m.active && <Check className="h-4 w-4 text-redline-500 shrink-0" strokeWidth={2} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5" data-testid="portal-home-box">
              <span className="block text-[9px] uppercase tracking-[0.15em] text-sand-400">Your home</span>
              <span className="block font-heading font-bold text-white text-sm truncate">{homeLabel(me.household) || "—"}</span>
            </div>
          )}
        </div>
        )}
        <nav data-testid="portal-navigation" aria-label="Resident navigation" className="sidebar-nav flex-1 min-h-0 p-3 space-y-1 overflow-y-auto sidebar-scroll">
          {navFor(me.community).map((n) => (
            <SidebarHint key={n.label} collapsed={sidebarCollapsed} label={navLabel(n)} testId={`portal-nav-hint-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <NavLink to={joinBase(n.to)} end={n.end} aria-label={navLabel(n)} data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={navSel ? { "--sidebar-active-bg": navSel.bg, "--sidebar-active-fg": navSel.fg } : undefined}
              className="sidebar-nav-item flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-sand-300 hover:bg-white/5 hover:text-white">
              <n.icon className="h-4.5 w-4.5 shrink-0" strokeWidth={1.5} /> <span className="sidebar-label">{navLabel(n)}</span>
            </NavLink>
            </SidebarHint>
          ))}
        </nav>
        {num && (
          <div className="sidebar-footer p-3 border-t border-white/10 space-y-2">
            <div className="sidebar-label px-1 text-[10px] uppercase tracking-[0.18em] font-bold text-sand-400">Your concierge</div>
            <SidebarHint collapsed={sidebarCollapsed} label={`Text concierge: ${num}`} testId="portal-side-text-hint">
            <a data-testid="portal-side-text" aria-label={`Text concierge: ${num}`} href={`sms:${num}`} style={navSel ? { backgroundColor: navSel.bg, color: navSel.fg } : undefined}
              className={`sidebar-nav-item flex items-center gap-2 rounded-xl transition-colors font-semibold px-3 py-2.5 text-sm hover:opacity-90 ${navSel ? "" : "bg-redline-500 hover:bg-redline-600 text-white"}`}><MessageSquareText className="h-4 w-4" /> <span className="sidebar-label">Text {num}</span></a>
            </SidebarHint>
            <SidebarHint collapsed={sidebarCollapsed} label={`Call concierge: ${num}`} testId="portal-side-call-hint">
            <a data-testid="portal-side-call" aria-label={`Call concierge: ${num}`} href={`tel:${num}`} className="sidebar-nav-item flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors text-sand-100 font-semibold px-3 py-2.5 text-sm"><PhoneCall className="h-4 w-4" /> <span className="sidebar-label">Call</span></a>
            </SidebarHint>
          </div>
        )}
        <div className="sidebar-footer p-3 border-t border-white/10">
          <div className="sidebar-user flex items-center gap-3 px-2">
            <div className="sidebar-label h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={navSel ? { backgroundColor: navSel.bg, color: navSel.fg } : undefined}>
              <span className={navSel ? "" : "h-8 w-8 rounded-full bg-redline-500 text-white flex items-center justify-center"}>{first[0]}</span>
            </div>
            <div className={sidebarCollapsed ? "w-full" : "flex-1 min-w-0"}>
              <div className="sidebar-label text-sm text-white font-semibold truncate">{me.person?.name}</div>
              <SidebarHint collapsed={sidebarCollapsed} label="Sign out" testId="portal-logout-hint">
              <button data-testid="portal-logout" aria-label="Sign out" onClick={logout} className={`text-sand-400 hover:text-white flex items-center gap-1 ${sidebarCollapsed ? "w-full justify-center rounded-xl p-2 hover:bg-white/5" : "text-[11px]"}`}><LogOut className={sidebarCollapsed ? "h-5 w-5" : "h-3 w-3"} /> <span className="sidebar-label">Sign out</span></button>
              </SidebarHint>
            </div>
          </div>
        </div>
      </aside>

      <div data-testid="portal-content" data-sidebar-collapsed={sidebarCollapsed} className="sidebar-content flex-1 lg:ml-64 min-w-0">
        {me.impersonated && (
          <div data-testid="portal-impersonation-banner" className="sticky top-0 z-40 bg-amber-500 text-ink px-4 lg:px-8 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-sm font-semibold">
              Staff view — you're using the portal as <span className="font-bold">{me.person?.name || "this resident"}</span>. Actions here are real.
            </span>
            <button data-testid="portal-exit-impersonation" onClick={exitImpersonation}
              className="shrink-0 self-start sm:self-auto rounded-full bg-ink text-white font-semibold text-xs px-4 py-1.5 hover:bg-ink-700 transition-colors">
              Exit to dashboard
            </button>
          </div>
        )}
        <header className="h-14 bg-[#f7f4f0]/95 backdrop-blur border-b border-sand-200 sticky top-0 z-30 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <SidebarCollapseToggle collapsed={sidebarCollapsed} onToggle={toggleSidebarCollapsed} sidebarId="resident-sidebar" testId="portal-sidebar-collapse-toggle" />
            <button data-testid="portal-sidebar-toggle" onClick={() => setOpen(true)} className="lg:hidden text-ink p-1 -ml-1"><Menu className="h-6 w-6" strokeWidth={1.5} /></button>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{me.community?.name}</span>
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu onOpenChange={(o) => { if (o && bell.unread > 0) markBellRead(); }}>
              <DropdownMenuTrigger asChild>
                <button data-testid="portal-bell" className="relative p-2 rounded-full text-ink-700 hover:text-ink hover:bg-sand-100 transition-colors">
                  <Bell className="h-5 w-5" strokeWidth={1.5} />
                  {bell.unread > 0 && (
                    <span data-testid="portal-bell-badge" className="absolute -top-0.5 -right-0.5 min-w-4.5 h-4.5 px-1 rounded-full bg-redline-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {bell.unread > 9 ? "9+" : bell.unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0 overflow-hidden">
                <div className="px-4 py-2.5 font-heading font-bold text-sm border-b border-sand-100">Notifications</div>
                <div className="max-h-80 overflow-y-auto" data-testid="portal-bell-list">
                  {bell.items.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nothing yet — announcements and request updates land here.</div>
                  ) : bell.items.map((n) => (
                    <button key={n.id} onClick={() => navigate(rebase(n.to))}
                      className="w-full text-left px-4 py-3 hover:bg-sand-50 transition-colors border-b border-sand-50 flex items-start gap-2.5">
                      <span className={`mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${n.kind === "safety" ? "bg-danger-100 text-danger-600" : n.kind === "ticket" ? "bg-sand-100 text-ink-600" : "bg-emerald-50 text-emerald-700"}`}>
                        {n.kind === "ticket" ? <Wrench className="h-3.5 w-3.5" strokeWidth={1.75} /> : n.kind === "safety" ? <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.75} /> : <Megaphone className="h-3.5 w-3.5" strokeWidth={1.75} />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-ink truncate">{n.title}</span>
                        {n.body && <span className="block text-xs text-muted-foreground line-clamp-2">{n.body}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="[&_button]:text-ink-700 [&_button:hover]:text-ink"><FeedbackWidget portal /></div>
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-5xl">
          <Outlet context={{ me }} />
        </main>
      </div>
    </div>
  );
}
